import { Injectable } from '@angular/core';
import { BaseProvider } from './base';
import { UserProvider, User } from './user';
import { API } from './api';
import { NumberProvider } from './number';
import * as StringUtils from '../utils/string';
import { TranslateService } from '@ngx-translate/core';

export interface UsageStat {
    used: number;
    unlimited: boolean;
    limit?: number | null;
    unit?: string;
    showLimit: boolean;
}

@Injectable()
export class APIUsageProvider extends BaseProvider {
    private userData: User;
    private dataLoaded = false;
    constructor(
        private userProvider: UserProvider,
        private numberProvider: NumberProvider,
        private api: API,
        private translate: TranslateService
    ) {
        super();
    }

    public async load(force: boolean = false): Promise<boolean> {
        await this.userProvider.load(force);
        this.userData = this.userProvider.getUserInformation();
        return true;
    }

    public parseUsageStatData(usedKey: string, limitKey: string, unit?: string): UsageStat {
        const usageStat: UsageStat = {
            used: this.userData.usageStatsTotals[usedKey],
            unlimited: this.userData.usageStatsLimits[limitKey] == -1 ? true : false,
            showLimit: true
        };
        if (unit != undefined) {
            usageStat.unit = unit;
        }
        if (!usageStat.unlimited) {
            usageStat.limit = this.numberProvider.round(this.userData.usageStatsLimits[limitKey], 2);
        }
        return usageStat;
    }

    public getStorageUsage(): UsageStat {
        return this.parseUsageStatData('diskSpace', 'Max Storage In GB', 'GB');
    }

    public getOrgUsersUsage(): UsageStat {
        return this.parseUsageStatData('usersInOrganization', 'Max Active Users');
    }

    public getBrainsUsage(): UsageStat {
        return this.parseUsageStatData('publishedBrains', 'Max Published Brains');
    }

    public getAPICallsUsage(): UsageStat {
        return this.parseUsageStatData('apiCalls', 'Max API Inferences Per Month');
    }

    /**
     *  Take information about an estimate (API usage, or time remaining), and return a pretty-printed value.
     * @param data The number we want to format.
     * @param unit The unit (if any) we want to attach to the formatted value.
     * @param precision The amount of rounding (if any) we should perform on the data.
     * @param longForm For time formatting, whether or not we want to use verbose time unit names.
     */
    public formatUsageUnit(
        data: number | null | undefined,
        unit: string | undefined,
        precision: number = 2,
        longForm = false
    ): string {
        let formatedDataWithUnit: { data: string; unit: string } = {
            data: '',
            unit: ''
        };

        switch (unit) {
            case undefined:
                formatedDataWithUnit = {
                    data: `${this.numberProvider.getCommaDelineatedNumStr(this.numberProvider.round(data, 2))}`,
                    unit: ''
                };
                break;
            case 'hr':
                formatedDataWithUnit = this.formatHourUnits(data);
                break;
            case 'sec':
                formatedDataWithUnit = this.formatSecondUnits(data, precision, longForm);
                break;
            default:
                formatedDataWithUnit = {
                    data: `${this.numberProvider.getCommaDelineatedNumStr(this.numberProvider.round(data, 2))}`,
                    unit
                };
                break;
        }

        return `${formatedDataWithUnit.data} ${formatedDataWithUnit.unit}`;
    }

    private formatHourUnits(data: number | null | undefined): { data: string; unit: string } {
        let formattedData;

        if (data === null || data == undefined) {
            return {
                data: '0',
                unit: 'hr'
            };
        }

        if (data < 1) {
            formattedData = {
                data: `${this.numberProvider.getCommaDelineatedNumStr(this.numberProvider.round(data * 60, 2))}`,
                unit: 'min'
            };
        } else if (data * 60 < 1) {
            formattedData = {
                data: `${this.numberProvider.getCommaDelineatedNumStr(this.numberProvider.round(data * 3600, 2))}`,
                unit: 'sec'
            };
        } else {
            formattedData = {
                data: `${this.numberProvider.getCommaDelineatedNumStr(this.numberProvider.round(data, 2))}`,
                unit: 'hr'
            };
        }

        return formattedData;
    }

    private formatSecondUnits(
        data: number | null | undefined,
        precision: number = 2,
        longForm: boolean
    ): { data: string; unit: string } {
        let formattedData;

        if (data === null || data == undefined) {
            return {
                data: '0',
                unit: 'sec'
            };
        }

        if (data < 60) {
            formattedData = {
                data: `${this.numberProvider.getCommaDelineatedNumStr(this.numberProvider.round(data, precision))}`,
                unit: longForm
                    ? data === 1
                        ? this.translate.instant('Second').toLowerCase()
                        : this.translate.instant('Seconds').toLowerCase()
                    : this.translate.instant('Sec').toLowerCase()
            };
        } else if (data >= 60 && data < 3600) {
            const timeInMinutes = this.numberProvider.round(data / 60, precision);
            formattedData = {
                data: `${this.numberProvider.getCommaDelineatedNumStr(
                    this.numberProvider.round(data / 60, precision)
                )}`,
                unit: longForm
                    ? timeInMinutes === 1
                        ? this.translate.instant('Minute').toLowerCase()
                        : this.translate.instant('Minutes').toLowerCase()
                    : this.translate.instant('Min').toLowerCase()
            };
        } else {
            const timeInHours = this.numberProvider.round(data / 60, precision);
            formattedData = {
                data: `${this.numberProvider.getCommaDelineatedNumStr(
                    this.numberProvider.round(data / 3600, precision)
                )}`,
                unit: longForm
                    ? timeInHours === 1
                        ? this.translate.instant('Hour').toLowerCase()
                        : this.translate.instant('Hours').toLowerCase()
                    : this.translate.instant('HR').toLowerCase()
            };
        }

        return formattedData;
    }

    public async getInferenceStatsForCurrentBillingPeriod(): Promise<any[]> {
        const now = new Date(Date.now());
        const endOfBillingPeriod = new Date(now);
        endOfBillingPeriod.setMonth(now.getMonth() + 1);
        endOfBillingPeriod.setDate(0);
        const startOfBillingPeriod = new Date(now);
        startOfBillingPeriod.setDate(1);
        return this.api
            .get(
                `inferences/stats?aggregationType=day&dateTo=${this.getStrFromDateObj(
                    endOfBillingPeriod
                )}&dateFrom=${this.getStrFromDateObj(startOfBillingPeriod)}`
            )
            .then(({ success, message }) => {
                if (success) {
                    const translateReplacements = {
                        month: now.toLocaleString('en-us', { month: 'long' })
                    };

                    return message.map(result => {
                        return {
                            ...result,
                            checked: false,
                            name: this.translate.instant('Activity for the month of [[month]]', translateReplacements)
                        };
                    });
                }
            });
    }

    public async getAllActivityForGivenPeriod({ dateFrom, dateTo, aggType }): Promise<any[]> {
        const urlSlug = `inferences/stats?aggregationType=${aggType}&dateTo=${dateTo}&dateFrom=${dateFrom}`;
        return this.api.get(urlSlug).then(({ success, message }) => {
            if (success) {
                return message.map(result => {
                    return {
                        ...result,
                        name: this.translate.instant('All Activity')
                    };
                });
            }
        });
    }

    public async getEnvironmentActivityForGivenDataset(id, aggType, dateTo, dateFrom): Promise<any[]> {
        return this.api
            .get(
                `inferences/stats?aggregationType=${aggType}&dateTo=${dateTo}&dateFrom=${dateFrom}&data[0][brainIdentifier]=${id}&data[0][environment]=staging&data[1][brainIdentifier]=${id}&data[1][environment]=production`
            )
            .then(({ success, message }) => {
                if (success) {
                    return message.map(result => {
                        return {
                            ...result,
                            name: StringUtils.capitalizeFirstLetter(result.inferenceInfo.environment)
                        };
                    });
                }
            });
    }

    public getStrFromDateObj(date): String {
        const yearStr = date.getFullYear();
        const monthStr = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : `${date.getMonth() + 1}`;
        const dayStr = date.getDate() + 1 < 10 ? `0${date.getDate()}` : `${date.getDate()}`;
        return `${yearStr}-${monthStr}-${dayStr}`;
    }
}



// WEBPACK FOOTER //
// ./src/providers/api_usage.ts